export function ensureZuluTime(date: string): string {
  if(!date) {
    date = new Date().toISOString();
  }
  
  if (!date.endsWith('Z')) {
    return `${date}Z`;
  }
  return date;
}

export function formatDate(date: string | undefined, timeZone: string = 'America/Montreal') {
  if (!date) {
    return '';
  }

  const dateValue = ensureZuluTime(date);
  
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: timeZone
  }).format(new Date(dateValue));
}

export function formatDateOnly(date: string | undefined, timeZone: string = 'America/Montreal') {
  if (!date) {
    return '';
  }

  const dateValue = ensureZuluTime(date);
  
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timeZone
  }).format(new Date(dateValue));
}

/**
 * Converts a Date object to an ISO string in the format "YYYY-MM-DDTHH:mm:ssZ"
 * 
 * @param date - The Date object to convert
 * @returns The formatted ISO string
 */
export function dateToISOZulu(date: Date): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error('Invalid Date object provided');
  }
  
  // Get date parts
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  
  // Get time parts
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  
  // Construct the ISO string
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
}

/**
 * Formats a date into a long format with spelled-out month like "15 juillet 2025"
 * 
 * @param date - The date string to format
 * @param locale - The locale to use for formatting (defaults to fr-CA)
 * @param timeZone - The timezone to use for formatting (defaults to America/Montreal)
 * @returns The formatted long date in the specified locale
 */
import { Ref, isRef } from 'vue';

export function formatLongDate(
  date: string | undefined, 
  locale: string | Ref<string> = 'fr-CA', 
  timeZone: string = 'America/Montreal'
): string {
  if (!date) {
    return '';
  }

  const dateValue = ensureZuluTime(date);
  const resolvedLocale = isRef(locale) ? locale.value : locale;

  return new Intl.DateTimeFormat(resolvedLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timeZone
  }).format(new Date(dateValue));
}
